import { useEffect } from 'react'

import { useClipboard } from '@brands/ui/hooks'
import { Box, IconButton, Link, Typography } from '@mui/material'
import { QRCodeSVG } from 'qrcode.react'

import { Copy04Icon } from '@/components/ui/icons'
import { Tooltip } from '@/components/ui/tooltip'
import { app } from '@/states/app-state'

interface Props {
  sharedKey: string
  authenticatorUri: string
}

const SCAN_TEXT = 'Scan this code with your authenticator app.'

export function AuthenticatorQrPanel({ sharedKey, authenticatorUri }: Props) {
  const clipboard = useClipboard({ timeout: 500 })

  useEffect(() => {
    if (clipboard.copied) {
      app.showMessageToast({ variant: 'success', message: 'Setup key copied to clipboard' })
    } else if (clipboard.error) {
      app.showMessageToast({ variant: 'error', message: 'Failed to copy the setup key' })
    }
  }, [clipboard.copied, clipboard.error])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2">{SCAN_TEXT}</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, bgcolor: 'common.white', borderRadius: 1 }}>
        <QRCodeSVG value={authenticatorUri} size={200} />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant="body2">Cannot scan it? Enter this key in your app instead:</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', letterSpacing: 1, wordBreak: 'break-all' }}>
            {sharedKey}
          </Typography>
          <Tooltip title="Copy key">
            <IconButton size="small" onClick={() => clipboard.copy(sharedKey)}>
              <Copy04Icon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      {/* Kept next to the QR code so the app store link still works inside the native webview. */}
      <Link href={authenticatorUri} typography="body2" color="secondary.dark">
        Open in authenticator app
      </Link>
    </Box>
  )
}
